import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { startAlarm, playCelebration, playCountTick, beep } from "@/lib/sound";
import { burstGold, burstReward } from "@/lib/fx";
import { Dices, Timer, Cake, Siren, X, RotateCcw, Play, Pause } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { AvatarView } from "@/components/AvatarView";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const FACTORY_SCREENS = [
  { key: "alarma", emoji: "!", title: "¡ATENCIÓN!", subtitle: "TODOS DEBEN ESCUCHAR", description: "", kind: "alarm", color: "#FF3333", countdown: 0, duration: 0, penalty: 0, sound_url: "" },
  { key: "ordenadores", emoji: "💻", title: "ORDENADORES", subtitle: "Enciende y entra en tu sesión", description: "", kind: "screen", color: "#2962FF" },
  { key: "recoger", emoji: "📦", title: "RECOGER", subtitle: "Guarda todo en su sitio", description: "", kind: "screen", color: "#FF6D00" },
  { key: "trabajo", emoji: "📚", title: "TRABAJO", subtitle: "Modo concentración", description: "", kind: "screen", color: "#00C853" },
  { key: "juego", emoji: "🎮", title: "JUEGO", subtitle: "¡A disfrutar!", description: "", kind: "screen", color: "#AA00FF" },
];

const DockBtn = ({ onClick, children, testid, label, danger }) => (
  <button data-testid={testid} onClick={onClick} title={label}
    className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold shadow-lg border-2 transition-transform hover:scale-110 active:scale-95 ${danger ? "bg-red-500 text-white border-red-300 animate-pulse-slow" : "bg-card text-foreground border-border"}`}>
    {children}
  </button>
);

function Overlay({ children, onClose, bg, blink, t }) {
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className={`fixed inset-0 z-[90] flex flex-col items-center justify-center text-center p-6 ${blink ? "animate-alarm" : ""}`} style={{ background: bg }}>
        {children}
        <Button data-testid="overlay-back-btn" onClick={onClose} variant="secondary" className="mt-10 rounded-full h-14 px-8 text-base font-bold">← {t("backToClass")}</Button>
      </motion.div>
    </AnimatePresence>
  );
}

export function ClassTools({ classId, students, contextLabel, targetType, targetId, screens, reload }) {
  const { t } = useI18n();
  const list = (screens && screens.length ? screens : FACTORY_SCREENS);
  const alarm = list.find((s) => s.kind === "alarm") || FACTORY_SCREENS[0];
  const quick = list.filter((s) => s.kind !== "alarm");

  const [randOpen, setRandOpen] = useState(false);
  const [pool, setPool] = useState([]);
  const [picked, setPicked] = useState(null);
  const [timerOpen, setTimerOpen] = useState(false);
  const [tCfg, setTCfg] = useState({ m: 5, s: 0 });
  const [left, setLeft] = useState(null);
  const [running, setRunning] = useState(false);
  const [screen, setScreen] = useState(null);
  const [count, setCount] = useState(null);
  const [paused, setPaused] = useState(false);
  const stopRef = useRef(null);
  const penaltyRef = useRef(false);
  const [bdayOpen, setBdayOpen] = useState(false);
  const [bday, setBday] = useState(null);
  const [bdayStep, setBdayStep] = useState(0);

  // timer tick
  useEffect(() => {
    if (!running || left === null) return;
    if (left <= 0) { setRunning(false); beep(); beep(); toast.success(`⏱ ${t("timeUp")}`); return; }
    const id = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(id);
  }, [running, left]);

  // alarm countdown — the penalty is applied only when it reaches 00:00
  useEffect(() => {
    if (count === null || paused) return;
    if (count <= 0) { setCount(null); applyPenalty(); return; }
    playCountTick(count);
    const id = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [count, paused]);

  // birthday sequence
  useEffect(() => {
    if (!bday || bdayStep === 0) return;
    if (bdayStep <= 3) { playCountTick(4 - bdayStep); const id = setTimeout(() => setBdayStep((s) => s + 1), 900); return () => clearTimeout(id); }
    if (bdayStep === 4) { playCelebration(); burstGold(); burstReward(); }
  }, [bday, bdayStep]);

  const stopSound = () => { if (stopRef.current) { stopRef.current(); stopRef.current = null; } };

  const applyPenalty = () => {
    const sc = screen;
    if (!sc || penaltyRef.current) return;
    const pen = Math.abs(Number(sc.penalty) || 0);
    if (!pen) return;
    penaltyRef.current = true;
    api.adjustPoints(classId, { target_type: targetType, target_id: targetId, points: -pen, label: `${t("alarmKind")}: ${sc.title}`, emoji: sc.emoji || "!" })
      .then((r) => { toast.warning(`−${pen} pts · ${r.affected}`); reload && reload(); })
      .catch(() => {});
  };

  const openRandom = () => { if (!pool.length) setPool(students.map((s) => s.id)); setRandOpen(true); };
  const spin = () => {
    let p = pool.filter((id) => students.some((s) => s.id === id));
    if (!p.length) { p = students.map((s) => s.id); toast.info(t("resetList")); }
    const id = p[Math.floor(Math.random() * p.length)];
    setPicked(students.find((s) => s.id === id) || null);
    setPool(p.filter((x) => x !== id));
    beep();
  };

  const startTimer = () => { setLeft(Number(tCfg.m) * 60 + Number(tCfg.s)); setRunning(true); };
  const fmt = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;

  const openScreen = (sc) => {
    setScreen(sc);
    if (sc.kind === "alarm") {
      penaltyRef.current = false;
      setPaused(false);
      stopRef.current = startAlarm(sc.sound_url);
      const cd = Number(sc.countdown) || 0;
      if (cd > 0) setCount(cd);
      else if (Number(sc.penalty) > 0) setCount(1);
    }
  };
  const togglePause = () => {
    if (paused) { stopRef.current = startAlarm(screen.sound_url); setPaused(false); }
    else { stopSound(); setPaused(true); }
  };
  const closeScreen = () => { stopSound(); setCount(null); setPaused(false); penaltyRef.current = false; setScreen(null); };

  const startBirthday = (s) => { setBday(s); setBdayOpen(false); setBdayStep(1); };
  const endBirthday = async () => {
    const s = bday;
    setBday(null); setBdayStep(0);
    if (s?.birthday_active) { await api.updateStudent(s.id, { birthday_active: false }); reload && reload(); }
  };

  const todayMD = new Date().toISOString().slice(5, 10);
  const isBdayToday = (s) => (s.birthday || "").slice(5, 10) === todayMD;
  const flagged = students.filter((s) => s.birthday_active || isBdayToday(s));

  return (
    <>
      <div className="fixed right-4 bottom-4 z-[70] flex flex-col items-end gap-2" data-testid="class-tools-dock">
        <div className="hidden sm:flex flex-col gap-2 items-end">
          {quick.map((sc) => (
            <DockBtn key={sc.key} testid={`tool-screen-${sc.key}`} label={sc.title} onClick={() => openScreen(sc)}>{sc.emoji}</DockBtn>
          ))}
        </div>
        <DockBtn testid="tool-birthday" label={t("birthdayTitle")} onClick={() => setBdayOpen(true)}>
          <span className="relative">🎂{flagged.length > 0 && <span className="absolute -top-1 -right-2 w-4 h-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center">{flagged.length}</span>}</span>
        </DockBtn>
        <DockBtn testid="tool-random" label={t("randomTitle")} onClick={openRandom}><Dices className="w-7 h-7" /></DockBtn>
        <DockBtn testid="tool-timer" label={t("timerTitle")} onClick={() => setTimerOpen(true)}><Timer className="w-7 h-7" /></DockBtn>
        <button data-testid="tool-alarm" onClick={() => openScreen(alarm)} title={t("alarm")}
          className="w-20 h-20 rounded-3xl bg-red-500 text-white shadow-xl border-4 border-red-300 flex flex-col items-center justify-center transition-transform hover:scale-110 active:scale-95">
          <Siren className="w-8 h-8" /><span className="text-[10px] font-extrabold tracking-wide">{t("alarm")}</span>
        </button>
      </div>

      {/* Randomizer */}
      <Dialog open={randOpen} onOpenChange={setRandOpen}>
        <DialogContent className="rounded-3xl max-w-md text-center">
          <DialogHeader><DialogTitle className="font-display text-2xl">🎲 {t("randomTitle")} · {contextLabel}</DialogTitle></DialogHeader>
          <div className="py-4 flex flex-col items-center gap-4">
            {picked ? (
              <motion.div key={picked.id} initial={{ scale: 0.6, rotate: -8 }} animate={{ scale: 1, rotate: 0 }} className="flex flex-col items-center gap-3">
                <AvatarView student={picked} size={140} plain />
                <div data-testid="random-picked" className="font-display text-3xl font-extrabold">{picked.name}</div>
              </motion.div>
            ) : <div className="text-muted-foreground py-8">{t("spinHint")}</div>}
            <div className="text-xs text-muted-foreground">{t("remaining")} {pool.length} {t("of")} {students.length}</div>
            <div className="flex gap-2">
              <Button data-testid="random-spin-btn" onClick={spin} className="rounded-full h-12 px-7 text-base font-bold">{t("spin")}</Button>
              <Button data-testid="random-reset-btn" variant="outline" onClick={() => { setPool(students.map((s) => s.id)); setPicked(null); }} className="rounded-full h-12 gap-2"><RotateCcw className="w-4 h-4" /> {t("resetList")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timer */}
      <Dialog open={timerOpen} onOpenChange={(v) => { setTimerOpen(v); if (!v) setRunning(false); }}>
        <DialogContent className="rounded-3xl max-w-md text-center">
          <DialogHeader><DialogTitle className="font-display text-2xl">⏱ {t("timerTitle")}</DialogTitle></DialogHeader>
          <div className="py-4 flex flex-col items-center gap-5">
            <div data-testid="timer-display" className={`font-mono font-extrabold text-6xl ${left === 0 ? "text-red-500" : ""}`}>{fmt(left === null ? Number(tCfg.m) * 60 + Number(tCfg.s) : left)}</div>
            <div className="flex items-center gap-2">
              {[1, 2, 5, 10, 15].map((m) => <button key={m} data-testid={`timer-preset-${m}`} onClick={() => { setTCfg({ m, s: 0 }); setLeft(m * 60); setRunning(false); }} className="px-4 h-11 rounded-full bg-muted font-bold hover:bg-accent">{m}′</button>)}
            </div>
            <div className="flex items-center gap-2">
              <input data-testid="timer-min" type="number" min="0" value={tCfg.m} onChange={(e) => setTCfg({ ...tCfg, m: e.target.value })} className="w-20 h-12 rounded-xl bg-muted text-center font-bold text-lg" />
              <span className="font-bold">{t("minutes")}</span>
              <input data-testid="timer-sec" type="number" min="0" max="59" value={tCfg.s} onChange={(e) => setTCfg({ ...tCfg, s: e.target.value })} className="w-20 h-12 rounded-xl bg-muted text-center font-bold text-lg" />
              <span className="font-bold">{t("seconds")}</span>
            </div>
            <div className="flex gap-2">
              {running ? <Button data-testid="timer-pause-btn" onClick={() => setRunning(false)} className="rounded-full h-12 px-7 gap-2"><Pause className="w-4 h-4" /> {t("pause")}</Button>
                : <Button data-testid="timer-start-btn" onClick={() => (left && left > 0 ? setRunning(true) : startTimer())} className="rounded-full h-12 px-7 gap-2"><Play className="w-4 h-4" /> {t("start")}</Button>}
              <Button data-testid="timer-reset-btn" variant="outline" onClick={() => { setRunning(false); setLeft(Number(tCfg.m) * 60 + Number(tCfg.s)); }} className="rounded-full h-12 gap-2"><RotateCcw className="w-4 h-4" /> {t("reset")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Birthday picker */}
      <Dialog open={bdayOpen} onOpenChange={setBdayOpen}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">🎂 {t("birthdayTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            {flagged.length > 0 && (
              <div>
                <div className="text-sm font-bold mb-2">{t("todayMarked")}</div>
                <div className="flex flex-wrap gap-2">
                  {flagged.map((s) => <button key={s.id} data-testid={`bday-pick-${s.id}`} onClick={() => startBirthday(s)} className="flex items-center gap-2 p-2 rounded-2xl border-2 border-primary bg-accent font-bold"><AvatarView student={s} size={34} plain /> {s.name} 🎂</button>)}
                </div>
              </div>
            )}
            <div>
              <div className="text-sm font-bold mb-2">{t("allStudents")}</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-60 overflow-auto scrollbar-thin">
                {students.map((s) => <button key={s.id} data-testid={`bday-any-${s.id}`} onClick={() => startBirthday(s)} className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-accent"><AvatarView student={s} size={44} plain /><span className="text-xs font-semibold truncate w-full text-center">{s.name}</span></button>)}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen alarm / quick screens */}
      {screen && (
        <Overlay onClose={closeScreen} t={t} bg={screen.kind === "alarm" ? "#B00020" : (screen.color || "#111") } blink={screen.kind === "alarm" && !paused}>
          <div data-testid="screen-overlay" className="text-white flex flex-col items-center gap-4">
            <div className="text-[5rem] sm:text-[8rem] leading-none">{screen.emoji}</div>
            <div className="font-display text-4xl sm:text-6xl font-extrabold">{screen.title}</div>
            {screen.subtitle && <div className="text-lg sm:text-2xl font-bold opacity-90">{screen.subtitle}</div>}
            {screen.description && <div className="text-base max-w-xl opacity-80">{screen.description}</div>}
            {count !== null && <div data-testid="screen-countdown" className="font-mono text-6xl font-extrabold mt-2">{count}</div>}
            {screen.kind === "alarm" && <Button data-testid="stop-alarm-btn" onClick={togglePause} className="rounded-full h-14 px-8 text-base font-extrabold bg-white text-red-600 hover:bg-white/90">{paused ? `▶ ${t("start")}` : `⏸ ${t("pause")}`}</Button>}
          </div>
        </Overlay>
      )}

      {/* Birthday celebration */}
      {bday && (
        <Overlay onClose={endBirthday} t={t} bg="linear-gradient(135deg,#2b0b3a,#5b1d6e)">
          <div className="text-white flex flex-col items-center gap-5">
            <div className="font-display text-3xl sm:text-5xl font-extrabold">🎉 {t("happyBirthday")}</div>
            <AvatarView student={bday} size={140} plain />
            <div data-testid="bday-name" className="font-display text-4xl sm:text-6xl font-extrabold">{bday.name}</div>
            {bdayStep <= 3 ? (
              <motion.div key={bdayStep} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="font-mono text-7xl font-extrabold">{4 - bdayStep}</motion.div>
            ) : (
              <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} className="flex flex-col items-center gap-2">
                <motion.div animate={{ y: [0, -14, 0] }} transition={{ repeat: Infinity, duration: 1.4 }} className="text-[7rem] leading-none">🎂</motion.div>
                <div className="text-xl font-bold">{t("blowCandles")} 🕯️</div>
              </motion.div>
            )}
          </div>
        </Overlay>
      )}
    </>
  );
}
